import { useCallback, useEffect, useState } from 'react';

import { projectService } from '../../services/projectService';

import { ConnectionStatus } from './types';

export const useGitHubConnect = (
  isOpen: boolean,
  projectId?: string | null,
  onConnectedProject?: (projectId: string) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      setIsLoading(true);
      const status = await projectService.getGitHubStatus(projectId);

      setConnectionStatus(status);
    } catch (err: unknown) {
      console.error('Failed to fetch GitHub status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (isOpen && projectId) {
      fetchStatus();
    }
  }, [isOpen, projectId, fetchStatus]);

  const connect = async (
    repoUrl: string,
    username: string,
    token: string,
    createRepoIfMissing: boolean
  ) => {
    try {
      setIsLoading(true);
      const payload = {
        repo_url: repoUrl,
        username: username || undefined,
        access_token: token || undefined,
        create_repo_if_missing: createRepoIfMissing,
      };

      if (projectId) {
        await projectService.connectGitHub(projectId, payload);
        await fetchStatus();
      } else {
        const result = await projectService.createProjectFromGitHub(payload);
        if (result.project_id) onConnectedProject?.(result.project_id);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const sync = async (direction: 'source_to_mirror' | 'mirror_to_source') => {
    if (!projectId) return;
    try {
      setIsLoading(true);
      setSyncMessage(null);
      const result = await projectService.syncGitHub(projectId, { direction });
      setSyncMessage(`Synced ${result.default_branch} (${direction === 'source_to_mirror' ? 'GitHub → 8080 mirror' : '8080 mirror → GitHub'}).`);
      await fetchStatus();
    } finally {
      setIsLoading(false);
    }
  };

  const disconnect = async () => {
    if (!projectId) return;
    try {
      setIsLoading(true);
      await projectService.disconnectGitHub(projectId);
      await fetchStatus();
    } catch (err: unknown) {
      console.error('Failed to disconnect:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, connectionStatus, connect, disconnect, sync, syncMessage };
};
